import { useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import Table from "Shared/components/DataTable/Table"
import restClient from 'Shared/hooks/restClient'
import useCurrency from "Shared/hooks/useCurrency"
import "./DonationsWidget.scoped.scss"

const DonationsWidget = () => {
  const target = useSel((s) => s.target)
  const [refreshTable, setRefreshTable] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const { currency, currencySymbol } = useCurrency()

  const isTargetValid = target && Object.keys(target).length > 0 && target.id

  const validationSchema = Yup.object({
    amount: Yup.number()
      .required('Amount is required')
      .min(1, `Minimum donation amount is ${currencySymbol}1.00`)
      .positive('Please enter a donation amount above 0'),
    dateDonated: Yup.date()
      .required('Date donated is required'),
    source: Yup.string()
  })

  const initialValues = {
    amount: '',
    dateDonated: '',
    source: ''
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    restClient.post('/api/donations', {
      amount: parseFloat(values.amount),
      donatedAt: values.dateDonated,
      source: values.source,
      targetId: target.id,
      currency: currency,
      externalId: `OFFLINE-${Date.now()}`,
    })
      .then(() => {
        resetForm()
        setRefreshTable(prev => !prev)
        setShowForm(false) // Collapse form after successful submission
      })
      .catch((error) => {
        console.error('Error adding donation:', error)
      })
      .finally(() => {
        setSubmitting(false)
      })
  }

  const handleDelete = (donation) => {
    if (confirm('Are you sure you want to delete this donation?')) {
      restClient
        .delete(`/api/donations/${donation.id}`)
        .then(() => {
          setRefreshTable(prev => !prev)
        })
    }
  }

  if (!isTargetValid) {
    return <div>Loading...</div>
  }

  const handleTableClick = (e) => {
    if (e.target.classList.contains('delete-donation')) {
      handleDelete({ id: e.target.dataset.donationId })
    }
  }

  const toggleForm = (e) => {
    e.preventDefault()
    setShowForm(!showForm)
  }

  return (
    <>
      <p className="larger bold no-margin-top">Donations</p>
      <Table
        key={refreshTable}
        table="donations"
        language={{ emptyTable: "No donations made." }}
        scope={{ target_id: target.id }}
        hideSearch={true}
        onClick={handleTableClick}
      />
      <div className="margin-top double">
        <a
          href="#"
          className="button secondary small"
          onClick={toggleForm}
        >
          Log an offline donation
        </a>

        {showForm && (
          <div className="margin-top">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (
                <Form>
                  <div className="donation-fields">
                    <div className='donation-field'>
                      <label className='bold margin-bottom half'>Amount *</label>
                      <div className="amount-input">
                        {currencySymbol}
                        <Field
                          name="amount"
                          type="number"
                          className="input margin-left half"
                          step="0.01"
                          min="1"
                          placeholder="0.00"
                        />
                      </div>
                      <ErrorMessage
                        name="amount"
                        component="span"
                        className="error error-amount margin-top half"
                      />
                    </div>

                    <div className='donation-field'>
                      <label className='bold margin-bottom half'>Date Donated *</label>
                      <Field
                        name="dateDonated"
                        type="date"
                        className="input"
                      />
                      <ErrorMessage
                        name="dateDonated"
                        component="span"
                        className="error margin-top half"
                      />
                    </div>

                    <div className='donation-field'>
                      <label className='bold margin-bottom half'>Source</label>
                      <Field
                        name="source"
                        type="text"
                        className="input"
                        placeholder="e.g. Movement"
                      />
                    </div>
                  </div>

                  <div>
                    <button
                      type="submit"
                      className="button primary small margin-top"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? 'Logging...' : 'Log donation'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        )}
      </div>
    </>
  )
}

export default DonationsWidget